<script setup>
import {ref, computed, nextTick, onBeforeUnmount, onMounted, watch} from 'vue';
import {useAssistantMarkdown} from '~/composables/useAssistantMarkdown';
import {useAssistant} from '~/composables/useAssistant';
import {useAuthService} from '~/composables/useAuthService';
import {useAssistantPanel} from '~/composables/useAssistantPanel';
import TappyAvatar from '~/components/assistant/TappyAvatar.vue';
import {setupHandOverLabel, TAPPY_ONE_SHOT_MS, tappyMessageAnimation, tappyWords} from '~/utils/tappy';

// Assistant replies contain markdown (the prompt asks for **bold** labels and
// numbered steps). Raw HTML stays escaped (markdown-it default). User messages
// stay plain text.
//
// Links come in two kinds. Help-doc product links are RELATIVE paths
// ("/admin", "/clubs") — environment-agnostic by design — and navigate
// same-tab through the router, so the docked panel stays open over the page
// it just sent the user to. External links open in a new tab.
// Doc links may carry a {clubId} placeholder ("/admin/clubs/{clubId}/members")
// so docs can point at club-scoped pages without knowing anyone's ids. It is
// filled from the club in context: the club being viewed, else the user's club
// when they administer exactly one. With no club (or a choice to make between
// several) the link is UNRESOLVABLE and is rendered as plain text — a dead link
// is worse than no link.
const route = useRoute();
const {user} = useAuthService();
const clubIdInContext = computed(() => {
  const fromRoute = route.params?.clubId;
  if (fromRoute) return String(fromRoute);
  const adminFor = user.value?.adminFor;
  return adminFor?.length === 1 ? String(adminFor[0]) : null;
});

// Rendering lives in useAssistantMarkdown so the live chat and the super-admin
// transcript view cannot drift apart about what a reply looks like.
const {renderMarkdown} = useAssistantMarkdown(() => clubIdInContext.value);

// v-html anchors bypass vue-router, so intercept internal-link clicks and
// route them (a full page load would drop the panel and re-bootstrap the app).
const router = useRouter();
const onMarkdownClick = (event) => {
  const anchor = event.target?.closest?.('a');
  if (!anchor) return;
  const href = anchor.getAttribute('href') || '';
  if (!href.startsWith('/')) return;
  event.preventDefault();
  router.push(href);
};

const props = defineProps({
  // Dialog context: the overlay provides its own title bar, and the chat can
  // use more vertical space.
  hideHeader: {type: Boolean, default: false},
});

const {
  messages,
  streaming,
  limitNotice,
  notice,
  greeting,
  retryNotice,
  sendMessage,
  rateMessage,
  newConversation,
} = useAssistant();

// Grant 2026-11-02: Tappy's fixed words in the device's language (TappyGreetings, served by the API).
// English until the greeting has loaded, so the chat never opens blank.
const words = computed(() => tappyWords(greeting.value));
const intro = computed(() => words.value.intro);
const aiLabel = computed(() => words.value.aiLabel);
const anyLanguageLine = computed(() => words.value.anyLanguageLine);

// Tappy's hand-over: "Open the Setup assistant" under a reply that offered it, one button per club the
// admin can open it for. The docked panel closes so the page is seen.
const {close: closePanel} = useAssistantPanel();
const openSetupAssistant = (club) => {
  closePanel();
  router.push({name: 'ClubSetupAssistant', params: {clubId: club.id}});
};
const handOverLabel = (message, club) => setupHandOverLabel(message.setupAssistant.clubs, club);

// Grant 2026-11-02: Tappy's moves, tied to the chat's moments (utils/tappy tappyMessageAnimation has the rules).
// - Hello: the greeting bubble's avatar, every time the empty chat shows it (first open, after New chat).
// - Peek: the header avatar when the chat comes on screen (this card's own header on /help; the docked panel's head
//   does the same from AssistantPanel). The header's move is shared state so New chat can make it wink from here.
// - Thinking → Talking: the in-progress reply's avatar, until its first words, then while they stream.
// - Bounce: that reply's avatar once, when the reply finishes.
// - Wink: a reply's avatar when it is rated up; the header avatar on New chat (the sign-off).
// A one-shot is held for its own length and then dropped, so the next one for the same bubble can play again.
const headerAnimation = useState('tappyHeaderAnimation', () => 'idle');
const finished = ref(null);
const winking = ref(null);
const oneShotTimers = {};
const playOnce = (target, message, animation) => {
  target.value = message;
  if (oneShotTimers[animation]) clearTimeout(oneShotTimers[animation]);
  oneShotTimers[animation] = setTimeout(() => {
    oneShotTimers[animation] = null;
    if (target.value === message) target.value = null;
  }, TAPPY_ONE_SHOT_MS[animation]);
};
const messageAnimation = (message) => tappyMessageAnimation(message, {finished: finished.value, winking: winking.value});
watch(streaming, (now, was) => {
  if (!was || now) return;
  const last = messages.value[messages.value.length - 1];
  if (last?.role === 'assistant' && last.text && !last.error) playOnce(finished, last, 'bounce');
});
const rate = (message, rating) => {
  if (rating === 1 && message.rating !== 1) playOnce(winking, message, 'wink');
  return rateMessage(message, rating);
};
const startNewChat = () => {
  if (streaming.value) return;
  newConversation();
  headerAnimation.value = 'wink';
};
onMounted(() => {
  if (!props.hideHeader) headerAnimation.value = 'peek';
});
onBeforeUnmount(() => {
  for (const key of Object.keys(oneShotTimers)) {
    if (oneShotTimers[key]) clearTimeout(oneShotTimers[key]);
  }
});

const draft = ref('');
const scroller = ref(null);
const inputRef = ref(null);
const maxChars = 1000;

const submit = async () => {
  // Field stays enabled while a reply streams (so it never loses focus); the
  // send is gated instead. Guard here so Enter mid-stream doesn't clear the box.
  // Also gated on the notice: no disclosure on screen, no sending.
  if (streaming.value || !notice.value) return;
  const text = draft.value.trim();
  if (!text) return;
  draft.value = '';
  inputRef.value?.focus();
  await sendMessage(text);
};

const scrollToBottom = async () => {
  await nextTick();
  if (scroller.value) {
    scroller.value.scrollTop = scroller.value.scrollHeight;
  }
};

// Keep the newest message in view while streaming, and land at the bottom when
// the popup is (re)opened with an existing conversation.
watch(messages, scrollToBottom, {deep: false});
onMounted(scrollToBottom);
</script>

<template>
  <div class="tl-card assistant-card">
    <div v-if="!hideHeader || messages.length > 0" class="assistant-header">
      <div v-if="!hideHeader" class="assistant-title">
        <!-- Grant 2026-11-02: Tappy's face; it peeks in when the chat opens, winks on New chat. -->
        <tappy-avatar :size="30" :animation="headerAnimation" @settled="headerAnimation = 'idle'"/>
        <span>Tappy</span>
        <span class="assistant-title__sub">{{ aiLabel }}</span>
      </div>
      <v-spacer v-else/>
      <v-btn
          v-if="messages.length > 0"
          size="small"
          variant="text"
          prepend-icon="mdi-autorenew"
          @click="startNewChat"
      >
        New chat
      </v-btn>
    </div>

    <div ref="scroller" class="assistant-messages" :class="{'assistant-messages--tall': hideHeader}">
      <!-- Monitoring / data-use notice. Always the first thing in the thread (it
           stays at the top as the conversation grows), so consent is visible
           before the first message is sent. -->
      <div class="assistant-notice">
        <p v-if="notice">{{ notice }}</p>
        <p v-else>
          Couldn't load the chat right now.
          <a href="#" @click.prevent="retryNotice">Try again</a>
        </p>
      </div>

      <!-- Grant 2026-11-02: Tappy's first message, in the device's language, as a reply bubble (no rating). -->
      <div v-if="messages.length === 0" class="assistant-row assistant">
        <!-- Hello: Tappy's first introduction, bigger than the avatars beside later replies (the Intro design). -->
        <tappy-avatar class="assistant-avatar assistant-avatar--intro" :size="56" animation="hello"/>
        <div class="assistant-bubble assistant">
          <div class="assistant-md"><p>{{ intro }}</p><p v-if="anyLanguageLine">{{ anyLanguageLine }}</p></div>
        </div>
      </div>

      <div
          v-for="(message, index) in messages"
          :key="message.id || index"
          class="assistant-row"
          :class="message.role"
      >
        <!-- Still beside every reply but the one in progress, which thinks, talks, then bounces once. -->
        <tappy-avatar
            v-if="message.role === 'assistant'"
            class="assistant-avatar"
            :size="24"
            :animation="messageAnimation(message)"
        />
        <div class="assistant-bubble" :class="message.role">
          <span v-if="message.role === 'assistant' && message.pending && !message.text" class="assistant-typing">
            Thinking…
          </span>
          <!-- A failure with nothing streamed: say why, in the bubble. Rendering
               empty markdown leaves a bubble with nothing in it. -->
          <div v-else-if="message.role === 'assistant' && !message.text && message.error"
               class="assistant-empty">{{ message.error }}</div>
          <!-- eslint-disable-next-line vue/no-v-html — markdown-it escapes raw HTML -->
          <div v-else-if="message.role === 'assistant'" class="assistant-md" @click="onMarkdownClick" v-html="renderMarkdown(message.text)"/>
          <span v-else class="assistant-text">{{ message.text }}</span>

          <div v-if="message.error && message.text" class="assistant-error">{{ message.error }}</div>

          <!-- Tappy's hand-over to the Setup assistant (one button per club it can be opened for). -->
          <div v-if="message.role === 'assistant' && message.setupAssistant?.clubs?.length" class="assistant-handover">
            <v-btn
                v-for="club in message.setupAssistant.clubs"
                :key="club.id"
                size="small"
                color="primary"
                variant="tonal"
                prepend-icon="mdi-creation-outline"
                @click="openSetupAssistant(club)"
            >
              {{ handOverLabel(message, club) }}
            </v-btn>
          </div>

          <div
              v-if="message.role === 'assistant' && message.id && !message.pending && !message.error"
              class="assistant-actions"
          >
            <v-btn
                icon
                size="x-small"
                variant="text"
                :color="message.rating === 1 ? 'primary' : undefined"
                @click="rate(message, 1)"
            >
              <v-icon size="16">mdi-thumb-up-outline</v-icon>
            </v-btn>
            <v-btn
                icon
                size="x-small"
                variant="text"
                :color="message.rating === -1 ? 'primary' : undefined"
                @click="rate(message, -1)"
            >
              <v-icon size="16">mdi-thumb-down-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Any 429 lands here, including codes this build has never heard of: the
         server owns the wording, so a new limit explains itself without a release.
         "Start a new chat" is ONLY for conversationFull — for a daily limit or the
         org-wide spend cap it fixes nothing and reads as blaming the user. -->
    <v-alert
        v-if="limitNotice"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-2"
    >
      {{ limitNotice.message }}
      <template v-if="limitNotice.code === 'conversationFull'">
        <v-btn size="small" variant="text" @click="startNewChat">Start a new chat</v-btn>
      </template>
    </v-alert>

    <v-form @submit.prevent="submit">
      <v-textarea
          ref="inputRef"
          v-model="draft"
          rows="1"
          auto-grow
          max-rows="4"
          :counter="maxChars"
          :maxlength="maxChars"
          variant="outlined"
          density="compact"
          hide-details="auto"
          placeholder="Ask Tappy…"
          @keydown.enter.exact.prevent="submit"
      >
        <template #append-inner>
          <v-btn
              icon
              size="small"
              color="primary"
              variant="text"
              :disabled="streaming || !draft.trim() || !notice"
              @click="submit"
          >
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </template>
      </v-textarea>
    </v-form>

    <p class="assistant-disclaimer">
      Tappy is an AI helper and can make mistakes. For anything it can't help with,
      <NuxtLink :to="{ name: 'Contact' }">contact us</NuxtLink>.
    </p>
  </div>
</template>

<style scoped lang="scss">
.assistant-card {
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.assistant-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.assistant-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  font-size: 15px;

  &__sub {
    font-weight: 500;
    font-size: 12px;
    color: rgb(var(--v-theme-charcoal));
    opacity: 0.75;
  }
}

/* Grant 2026-11-02: Tappy's face (TappyAvatar) beside each of its bubbles; bigger for the greeting. */
.assistant-avatar {
  margin-right: 8px;
  margin-top: 6px;

  &--intro {
    margin-top: 0;
    margin-right: 10px;
  }
}

.assistant-handover {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.assistant-messages {
  overflow-y: auto;
  max-height: 420px;
  min-height: 160px;

  &--tall {
    max-height: none;
    flex: 1;
  }
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 2px 12px;
}

.assistant-notice {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  padding: 10px 12px;
  color: rgb(var(--v-theme-charcoal));
  font-size: 11.5px;
  line-height: 1.55;

  p {
    margin: 0;
    // The notice is served as plain text and rendered verbatim — the client
    // doesn't parse its structure, so preserve the author's line breaks.
    white-space: pre-line;
  }

  a {
    color: rgb(var(--v-theme-primary));
  }
}

.assistant-empty {
  color: rgb(var(--v-theme-charcoal));
  font-size: 13.5px;
  line-height: 1.6;
  padding: 24px 8px;
  text-align: center;
}

.assistant-row {
  display: flex;

  &.user {
    justify-content: flex-end;
  }
}

.assistant-bubble {
  max-width: 85%;
  border-radius: 14px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.6;

  &.user {
    background: rgb(var(--v-theme-brandTint));
  }

  &.assistant {
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.assistant-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.assistant-md {
  word-break: break-word;

  :deep(p) {
    margin: 0 0 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(ol),
  :deep(ul) {
    margin: 0 0 8px;
    padding-left: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(li) {
    margin-bottom: 2px;
  }

  :deep(strong) {
    font-weight: 700;
  }

  :deep(code) {
    background: rgba(var(--v-theme-borderLight), 0.5);
    border-radius: 4px;
    padding: 1px 5px;
    font-size: 12.5px;
  }

  :deep(a) {
    color: rgb(var(--v-theme-primary));
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4) {
    font-size: 14px;
    font-weight: 700;
    margin: 0 0 6px;
  }
}

.assistant-typing {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgb(var(--v-theme-charcoal));
  font-size: 13px;
}

.assistant-empty {
  font-size: 0.85rem;
  opacity: 0.75;
}

.assistant-error {
  color: rgb(var(--v-theme-error));
  font-size: 12.5px;
  margin-top: 6px;
}

.assistant-actions {
  display: flex;
  gap: 2px;
  margin-top: 4px;
}

.assistant-disclaimer {
  font-size: 11.5px;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 8px;

  a {
    color: rgb(var(--v-theme-primary));
  }
}
</style>
